import React, { useState, useRef, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import {
  Drawer,
  Box,
  Stack,
  Grid,
  Divider,
  Button,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Card,
  CardContent,
  Skeleton,
} from '@mui/material'
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner'
import Webcam from 'react-webcam'
import { GoogleGenerativeAI } from '@google/generative-ai'
import { useGetCustomersLazyQuery } from 'services/customers'
import { useGetInboundTicketsQuery } from 'services/inboundTickets'
import { useUploadOrgImageMutation } from 'services/assets'
import { getImageUrl } from 'services/assets'
import { setSnackBar, showFormModal } from 'state/ui'
import { setCustomer } from 'state/customer'
import FormModalHeading from 'components/generic/modals/form-modal/FormModalHeading'
import Loading from 'components/generic/Loading'
import { b64toBlob } from 'utils/helpers'
import { useNavigate, useLocation } from 'react-router-dom'
import FORM_MODALS from 'utils/ui/modals'
import { LicenseData, isMobileDevice, parseAAMVABarcode, validateBarcodeQuality } from './utils'

// Type definitions
interface LicenseScanModalProps {
  isOpen: boolean
  onHideModal: () => void
  onCustomerSelect: (customer: any) => void
}

interface CustomerContact {
  firstName: string
  lastName: string
  firstNameLegal?: string
  lastNameLegal?: string
  licenseNumber?: string
  licenseExpiration?: string
  address1?: string
  address2?: string
  city?: string
  state?: string
  zip?: string
  birthDate?: string
  gender?: string
  isPrimary?: boolean
  licenseImage?: string
}

interface CustomerData extends Partial<LicenseData> {
  id?: string
  customerCommonIdentifierString: string
  supplierType?: string
  contacts?: CustomerContact[]
  isFromDatabase?: boolean
  originalLicenseData?: LicenseData
  isSupplier?: boolean
  accountType?: string
}

interface InboundTicket {
  id: string
  ticketId: string
  effectiveDate: string
  status: {
    value: string
  }
  materialTypeCommonNames?: string[]
  netWeight?: number
  netValue?: {
    commonString: string
  }
}

interface CameraDevice {
  deviceId: string
  label: string
  kind: string
}

type ScanMode = 'photo' | 'barcode'

// Initialize Gemini for photo processing only
const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_KEY as string)
const model = genAI.getGenerativeModel({ model: 'gemini-2.0-flash' })

const LicenseScanModal: React.FC<LicenseScanModalProps> = ({ isOpen, onHideModal, onCustomerSelect }) => {
  const dispatch = useDispatch()
  const webcamRef = useRef<Webcam>(null)
  const barcodeInputRef = useRef<HTMLInputElement>(null)
  const processingTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const scanningTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const focusTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const [scanMode, setScanMode] = useState<ScanMode>(() => {
    // Read from localStorage, default to 'photo' if not found
    try {
      return (localStorage.getItem('licenseScanMode') as ScanMode) || 'photo'
    } catch (error) {
      console.warn('Could not read from localStorage:', error)
      return 'photo'
    }
  })

  const [isCapturing, setIsCapturing] = useState<boolean>(false)
  const [capturedImage, setCapturedImage] = useState<string | null>(null)
  const [barcodeData, setBarcodeData] = useState<string>('')
  const [isScanning, setIsScanning] = useState<boolean>(false)
  const [isProcessing, setIsProcessing] = useState<boolean>(false)
  const [customerData, setCustomerData] = useState<CustomerData | null>(null)
  const [recentTickets, setRecentTickets] = useState<InboundTicket[]>([])
  const [customerResultsReturned, setCustomerResultsReturned] = useState<number | null>(null)
  const [lastProcessedBarcode, setLastProcessedBarcode] = useState<string>('')
  const [barcodeQualityIssues, setBarcodeQualityIssues] = useState<{
    issues: string[]
    suggestions: string[]
  } | null>(null)

  // Add camera switching state
  const [availableCameras, setAvailableCameras] = useState<CameraDevice[]>([])
  const [currentCameraId, setCurrentCameraId] = useState<string>('')
  const [cameraError, setCameraError] = useState<string | null>(null)

  // Add license image state
  const [licenseImageUrl, setLicenseImageUrl] = useState<string | null>(null)
  const [isLoadingLicenseImage, setIsLoadingLicenseImage] = useState<boolean>(false)
  const [uploadedLicenseImageFilename, setUploadedLicenseImageFilename] = useState<string | null>(null)

  const [uploadOrgImage] = useUploadOrgImageMutation()
  const [triggerGetCustomers, customerQueryResult] = useGetCustomersLazyQuery({
    refetchOnMountOrArgChange: true,
  })

  const ticketsQueryParams = {
    filter: JSON.stringify({
      search: customerData?.customerCommonIdentifierString || '',
      statuses: ['INCOMPLETE', 'PRICE_COMPLETE', 'APPROVED', 'SCALE_COMPLETE', 'PAID', 'TRUCK_IN_YARD'],
      sort: {
        field: 'effectiveDate',
        ascending: false,
      },
    }),
    page: 1,
    pageSize: 5,
  }

  const { data: inboundTicketsData } = useGetInboundTicketsQuery(ticketsQueryParams, {
    skip: !customerData?.customerCommonIdentifierString,
    refetchOnMountOrArgChange: true,
  })

  const navigate = useNavigate()
  const location = useLocation()

  // Enumerate available cameras when modal opens
  useEffect(() => {
    if (isOpen && scanMode === 'photo') {
      enumerateCameras()
    }
  }, [isOpen, scanMode])

  const enumerateCameras = async (): Promise<void> => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      const videoDevices = devices.filter(device => device.kind === 'videoinput') as CameraDevice[]

      setAvailableCameras(videoDevices)

      // If no current camera is set, default to environment camera or first available
      if (!currentCameraId && videoDevices.length > 0) {
        const environmentCamera = videoDevices.find(
          device => device.label.toLowerCase().includes('back') || device.label.toLowerCase().includes('environment'),
        )
        setCurrentCameraId(environmentCamera?.deviceId || videoDevices[0].deviceId)
      }

      setCameraError(null)
    } catch (error) {
      console.error('Error enumerating cameras:', error)
      setCameraError('Unable to access cameras')
    }
  }

  const switchCamera = (deviceId: string): void => {
    setCurrentCameraId(deviceId)
    setCameraError(null)
  }

  // Utility functions for specific camera types
  const switchToFrontCamera = (): void => {
    const frontCamera = availableCameras.find(
      camera =>
        camera.label.toLowerCase().includes('front') ||
        camera.label.toLowerCase().includes('user') ||
        camera.label.toLowerCase().includes('selfie'),
    )
    if (frontCamera) {
      switchCamera(frontCamera.deviceId)
    }
  }

  const switchToBackCamera = (): void => {
    const backCamera = availableCameras.find(
      camera =>
        camera.label.toLowerCase().includes('back') ||
        camera.label.toLowerCase().includes('environment') ||
        camera.label.toLowerCase().includes('rear'),
    )
    if (backCamera) {
      switchCamera(backCamera.deviceId)
    }
  }

  const handleError = (error: any, message: string): void => {
    dispatch(
      setSnackBar({
        snackbarType: 'error',
        snackbarMessage: error?.data?.error || message,
      }),
    )
  }

  const captureImage = (): void => {
    if (!webcamRef.current) return
    const imageSrc = webcamRef.current.getScreenshot()
    if (imageSrc) {
      setCapturedImage(imageSrc)
      setIsCapturing(false)
      processImage(imageSrc)
    }
  }

  const processImage = async (imageToProcess: string = capturedImage || ''): Promise<void> => {
    if (!imageToProcess) return

    setIsProcessing(true)
    try {
      // Convert base64 to blob
      const imageBlob = b64toBlob(imageToProcess)
      const formData = new FormData()
      formData.append('image', imageBlob)

      // Upload image
      const uploadResponse = await uploadOrgImage({ body: formData }).unwrap()

      // Store the uploaded filename for use when creating new customers
      if (uploadResponse?.imageName) {
        setUploadedLicenseImageFilename(uploadResponse.imageName)
      }

      // Process with Gemini
      const result = await model.generateContent([
        {
          inlineData: {
            data: imageToProcess.split(',')[1],
            mimeType: 'image/jpeg',
          },
        },
        {
          text: "Extract the following information from this driver's license: FirstName, LastName, LicenseNumber (Do not include any spaces in the LicenseNumber), ExpirationDate, Address1, Address2, City, State, Zip, BirthDate, Gender. Format the response as JSON.",
        },
      ])

      const response = await result.response
      const responseText = response.text()

      // Extract JSON from the markdown-formatted response
      const jsonMatch = responseText.match(/```json\n([\s\S]*?)\n```/)
      if (!jsonMatch) {
        throw new Error('Could not parse license data from response')
      }

      const licenseData: LicenseData = JSON.parse(jsonMatch[1])

      // Remove spaces from license number for searching
      const searchLicenseNumber = licenseData.LicenseNumber.replace(/\s+/g, '')

      // Search for customer by license number
      triggerGetCustomers({
        filter: JSON.stringify({
          search: searchLicenseNumber,
        }),
        page: 1,
        pageSize: 1,
      })

      // Store licenseData temporarily in case no customer is found
      setCustomerData({
        ...licenseData,
        customerCommonIdentifierString: `${licenseData.FirstName} ${licenseData.LastName}`.trim(),
      })
    } catch (error) {
      console.error('Error processing license:', error)
      handleError(error, 'There was an error processing the license')
    } finally {
      setIsProcessing(false)
    }
  }

  const processBarcodeData = async (barcodeText: string): Promise<void> => {
    if (!barcodeText) return

    // Validate barcode has meaningful content and appears to be AAMVA format
    if (barcodeText.length < 200) {
      // Don't log for short data - this is normal during progressive input
      return
    }

    // Check for AAMVA format markers and required fields
    if (
      !barcodeText.includes('ANSI') ||
      !barcodeText.includes('DAC') ||
      !barcodeText.includes('DCS') ||
      !barcodeText.includes('DAQ')
    ) {
      // Don't log for incomplete format - this is normal during progressive input
      return
    }

    // Validate barcode quality and provide feedback if issues are detected
    const qualityCheck = validateBarcodeQuality(barcodeText)
    if (!qualityCheck.isValid) {
      setBarcodeQualityIssues({
        issues: qualityCheck.issues,
        suggestions: qualityCheck.suggestions,
      })
    } else {
      setBarcodeQualityIssues(null)
    }

    // Prevent duplicate processing of the same barcode
    if (isProcessing || barcodeText === lastProcessedBarcode) {
      return
    }

    setIsProcessing(true)
    setIsScanning(false)
    setLastProcessedBarcode(barcodeText)

    // Reset customer results state for fresh search
    setCustomerResultsReturned(null)
    // Note: Don't clear recentTickets here - let the query handle it naturally

    try {
      // Use Gemini to parse barcode data (more flexible for international formats)
      const result = await model.generateContent([
        {
          text: `Parse the following driver's license barcode data and extract: FirstName, LastName, LicenseNumber (Do not include any spaces in the LicenseNumber), ExpirationDate, Address1, Address2, City, State, Zip, BirthDate, Gender. 

This is AAMVA format barcode data from a driver's license. Here are the common field codes to look for:
- DAC: FirstName
- DCS: LastName  
- DAQ: LicenseNumber (clean up any spaces)
- DBA: ExpirationDate
- DAG: Address1
- DAH: Address2
- DAI: City
- DAJ: State
- DAK: Zip
- DBB: BirthDate
- DBC: Gender (1=Male, 2=Female)

Please extract the information from these fields and format dates as MM/DD/YYYY. For Gender, convert 1 to "M" and 2 to "F". Clean up any extra periods, spaces, or formatting artifacts. Format the response as JSON.

Barcode data:
${barcodeText}`,
        },
      ])

      const response = await result.response
      const responseText = response.text()

      // Extract JSON from the markdown-formatted response
      const jsonMatch = responseText.match(/```json\n([\s\S]*?)\n```/)
      if (!jsonMatch) {
        throw new Error('Could not parse license data from response')
      }

      const licenseData: LicenseData = JSON.parse(jsonMatch[1])

      // Validate we got essential fields
      if (!licenseData.FirstName || !licenseData.LastName) {
        throw new Error('Could not extract required fields from barcode')
      }

      // Remove spaces from license number for searching
      const searchLicenseNumber = licenseData.LicenseNumber?.replace(/\s+/g, '') || ''

      if (searchLicenseNumber) {
        // Search for customer by license number
        triggerGetCustomers({
          filter: JSON.stringify({
            search: searchLicenseNumber,
          }),
          page: 1,
          pageSize: 1,
        })
      }

      // Store licenseData temporarily in case no customer is found
      setCustomerData({
        ...licenseData,
        customerCommonIdentifierString: `${licenseData.FirstName || ''} ${licenseData.LastName || ''}`.trim(),
      })
    } catch (error) {
      console.error('Error processing barcode:', error)

      // Fallback to local regex parsing if Gemini fails
      try {
        const licenseData = parseAAMVABarcode(barcodeText)

        if (licenseData.FirstName && licenseData.LastName) {
          const searchLicenseNumber = licenseData.LicenseNumber?.replace(/\s+/g, '') || ''

          if (searchLicenseNumber) {
            triggerGetCustomers({
              filter: JSON.stringify({
                search: searchLicenseNumber,
              }),
              page: 1,
              pageSize: 1,
            })
          }

          setCustomerData({
            ...licenseData,
            customerCommonIdentifierString: `${licenseData.FirstName || ''} ${licenseData.LastName || ''}`.trim(),
          } as CustomerData)
        } else {
          throw new Error('Fallback parsing also failed')
        }
      } catch (fallbackError) {
        console.error('Fallback parsing failed:', fallbackError)
        handleError(error, 'There was an error processing the barcode data')
      }
    } finally {
      setIsProcessing(false)
    }
  }

  const handleCloseModal = (): void => {
    // Clear any pending timeouts
    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current)
      processingTimeoutRef.current = null
    }
    if (scanningTimeoutRef.current) {
      clearTimeout(scanningTimeoutRef.current)
      scanningTimeoutRef.current = null
    }
    if (focusTimeoutRef.current) {
      clearTimeout(focusTimeoutRef.current)
      focusTimeoutRef.current = null
    }

    // Reset all state
    setIsCapturing(false)
    setCapturedImage(null)
    setBarcodeData('')
    setIsScanning(false)
    setIsProcessing(false)
    setCustomerData(null)
    setRecentTickets([])
    setCustomerResultsReturned(null)
    setLastProcessedBarcode('')
    setBarcodeQualityIssues(null)

    // Reset camera state
    setAvailableCameras([])
    setCurrentCameraId('')
    setCameraError(null)

    // Reset license image state
    setLicenseImageUrl(null)
    setIsLoadingLicenseImage(false)
    setUploadedLicenseImageFilename(null)

    // Clear the barcode input field
    if (barcodeInputRef.current) {
      barcodeInputRef.current.value = ''
    }

    // Reset the lazy query to clear any cached results
    customerQueryResult.reset?.()

    onHideModal()
  }

  const handleSelectCustomer = (): void => {
    if (!customerData) {
      console.error('Invalid customer data provided')
      return
    }

    try {
      if (customerResultsReturned && customerResultsReturned > 0) {
        // Existing customer found - validate required fields before setting customer
        if (!customerData.id) {
          console.error('Customer ID is required for existing customers')
          return
        }

        dispatch(
          setCustomer({
            id: customerData.id,
            customerCommonIdentifierString: customerData.customerCommonIdentifierString,
            supplierType: customerData.supplierType,
          }),
        )

        // Call the onCustomerSelect callback if provided (for compatibility)
        if (onCustomerSelect) {
          onCustomerSelect(customerData)
        }
      } else {
        // No customer found - create new customer with license data
        const licenseData = displayFields

        // Format data for the new customer modal
        const newCustomerData = {
          // Map license data to customer form fields
          contacts: [
            {
              firstName: licenseData.FirstName || '',
              lastName: licenseData.LastName || '',
              firstNameLegal: licenseData.FirstName || '',
              lastNameLegal: licenseData.LastName || '',
              licenseNumber: licenseData.LicenseNumber || '',
              licenseExpiration: licenseData.ExpirationDate || '',
              address1: licenseData.Address1 || '',
              address2: licenseData.Address2 || '',
              city: licenseData.City || '',
              state: licenseData.State || '',
              zip: licenseData.Zip || '',
              birthDate: licenseData.BirthDate || '',
              gender: licenseData.Gender || '',
              isPrimary: true,
              // Include the uploaded license image filename if available
              ...(uploadedLicenseImageFilename && { licenseImage: uploadedLicenseImageFilename }),
            },
          ],
          // Use the name for the customer as well
          customerCommonIdentifierString: `${licenseData.FirstName || ''} ${licenseData.LastName || ''}`.trim(),
          isSupplier: true,
          accountType: 'supplier', // Default to supplier type
          isNew: true,
        }

        dispatch(
          showFormModal({
            formModalName: FORM_MODALS.NEW_CUSTOMER,
            formModalProps: {
              type: 'supplier',
              licenseData: newCustomerData,
              initialData: newCustomerData,
            },
          }),
        )

        // Call the onCustomerSelect callback if provided (for compatibility)
        if (onCustomerSelect) {
          onCustomerSelect(newCustomerData)
        }
      }
    } catch (error) {
      console.error('Error handling customer selection:', error)
    }

    handleCloseModal()
  }

  const handleCreateNewTicket = (): void => {
    if (!customerData) return

    // Set customer in Redux so ticket page can pick it up
    dispatch(
      setCustomer({
        id: customerData.id,
        customerCommonIdentifierString: customerData.customerCommonIdentifierString,
        supplierType: customerData.supplierType || '',
      }),
    )
    navigate(
      `/app/purchases/ticket-library/new?customerId=${customerData.id}&customerName=${encodeURIComponent(customerData.customerCommonIdentifierString)}`,
    )
    handleCloseModal()
  }

  const handleTryAgain = (): void => {
    setCapturedImage(null)
    setIsProcessing(false)
    setIsCapturing(true)
  }

  const handleTakeAnotherPhoto = (): void => {
    setCapturedImage(null)
    setIsCapturing(true)
  }

  const handleModeChange = (event: React.MouseEvent<HTMLElement>, newMode: ScanMode | null): void => {
    if (newMode !== null) {
      // Clear any pending processing timeout
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current)
        processingTimeoutRef.current = null
      }

      // Save the preference to localStorage
      try {
        localStorage.setItem('licenseScanMode', newMode)
      } catch (error) {
        console.warn('Could not save to localStorage:', error)
      }

      setScanMode(newMode)
      setCapturedImage(null)
      setBarcodeData('')
      setIsScanning(false)
      setCustomerData(null)
      setRecentTickets([])
      setCustomerResultsReturned(null)
      setLastProcessedBarcode('')
      setIsProcessing(false)
      setBarcodeQualityIssues(null)

      // Reset the lazy query when switching modes
      customerQueryResult.reset?.()
    }
  }

  const handleBarcodeInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newValue = event.target.value
    setBarcodeData(newValue)

    // Show scanning feedback when data starts coming in
    if (newValue && !isScanning && !isProcessing) {
      setIsScanning(true)

      // Safety timeout to clear scanning state if it gets stuck
      if (scanningTimeoutRef.current) {
        clearTimeout(scanningTimeoutRef.current)
      }
      scanningTimeoutRef.current = setTimeout(() => {
        setIsScanning(false)
        scanningTimeoutRef.current = null
      }, 10000) // 10 second safety timeout
    }

    // Clear any existing timeout to prevent processing partial data
    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current)
    }

    // Auto-process after scanner finishes (fallback for scanners that don't send Enter)
    if (newValue.length > 200 && newValue.includes('ANSI')) {
      processingTimeoutRef.current = setTimeout(() => {
        const trimmedData = newValue.trim()
        if (trimmedData && !isProcessing && trimmedData !== lastProcessedBarcode) {
          processBarcodeData(trimmedData)
        } else {
          // Fallback: clear scanning state if processing didn't start
          setIsScanning(false)
        }
        processingTimeoutRef.current = null
      }, 500) // Wait 500ms after data stops changing
    }
  }

  const handleBarcodeInputKeyPress = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault()

      // Clear any existing timeout to prevent multiple calls
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current)
      }

      // Small delay to ensure the input value is fully updated
      processingTimeoutRef.current = setTimeout(() => {
        const trimmedData = barcodeData.trim()
        if (trimmedData && !isProcessing && trimmedData !== lastProcessedBarcode) {
          processBarcodeData(trimmedData)
        }
        processingTimeoutRef.current = null
      }, 200)
    }
  }

  const shouldShowUseCustomerButton = (): boolean => {
    const path = location.pathname
    return path.includes('/purchases/ticket-library/new') || /\/purchases\/ticket-library\/[^/]+$/.test(path)
  }

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      // Clear any pending timeouts
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current)
        processingTimeoutRef.current = null
      }
      if (scanningTimeoutRef.current) {
        clearTimeout(scanningTimeoutRef.current)
        scanningTimeoutRef.current = null
      }
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current)
        focusTimeoutRef.current = null
      }

      // Reset all state for fresh start
      customerQueryResult.reset?.()
      setCustomerData(null)
      setRecentTickets([])
      setCustomerResultsReturned(null)
      setBarcodeData('')
      setLastProcessedBarcode('')
      setIsProcessing(false)
      setBarcodeQualityIssues(null)
      setCapturedImage(null)
      setCameraError(null)

      // Clear the input field
      if (barcodeInputRef.current) {
        barcodeInputRef.current.value = ''
      }

      // Set scanning state to false initially
      setIsScanning(false) // Will become true when data starts coming in for barcode mode

      // Focus the barcode input if in barcode mode
      if (scanMode === 'barcode') {
        focusTimeoutRef.current = setTimeout(() => {
          if (barcodeInputRef.current) {
            barcodeInputRef.current.focus()
          }
          focusTimeoutRef.current = null
        }, 100)
      }
    }

    // Cleanup function to clear timeout if effect re-runs or component unmounts
    return () => {
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current)
        focusTimeoutRef.current = null
      }
    }
  }, [isOpen, scanMode]) // Add scanMode as dependency

  // Start camera automatically when modal opens and in photo mode
  useEffect(() => {
    if (isOpen && !capturedImage && scanMode === 'photo') {
      setIsCapturing(true)
    } else if (scanMode === 'barcode') {
      setIsCapturing(false)
    }
  }, [isOpen, capturedImage, scanMode])

  // Update recent tickets when inbound tickets data changes
  useEffect(() => {
    if (inboundTicketsData) {
      setRecentTickets(inboundTicketsData.Items || [])
    } else if (inboundTicketsData === null || inboundTicketsData === undefined) {
      // Clear tickets when query is reset or no data
      setRecentTickets([])
    }
  }, [inboundTicketsData])

  // Effect to process customerQueryResult.data
  useEffect(() => {
    if (customerQueryResult.data) {
      const resultsReturned = customerQueryResult.data.resultsReturned || 0
      setCustomerResultsReturned(resultsReturned)
      if (resultsReturned > 0 && customerQueryResult.data.Items?.[0]) {
        const customer = customerQueryResult.data.Items[0]

        // Use database customer data as primary, preserve license scan data for reference only
        setCustomerData(prevData => ({
          ...customer, // Database customer info is primary
          // Preserve original license scan data separately for reference
          originalLicenseData: {
            FirstName: prevData?.FirstName,
            LastName: prevData?.LastName,
            LicenseNumber: prevData?.LicenseNumber,
            ExpirationDate: prevData?.ExpirationDate,
            Address1: prevData?.Address1,
            Address2: prevData?.Address2,
            City: prevData?.City,
            State: prevData?.State,
            Zip: prevData?.Zip,
            BirthDate: prevData?.BirthDate,
            Gender: prevData?.Gender,
          },
          // Ensure we maintain the customer database structure
          isFromDatabase: true,
        }))
      }

      // Ensure scanning state is cleared when customer query completes
      setIsScanning(false)

      // Clear scanning timeout since processing completed successfully
      if (scanningTimeoutRef.current) {
        clearTimeout(scanningTimeoutRef.current)
        scanningTimeoutRef.current = null
      }
    } else if (customerQueryResult.data === undefined && customerQueryResult.isLoading === false) {
      // Query was reset or cleared
      setCustomerResultsReturned(null)
    }
  }, [customerQueryResult.data, customerQueryResult.isLoading])

  // Effect to load license image when customer data is available
  useEffect(() => {
    const loadLicenseImage = async () => {
      // Reset license image state
      setLicenseImageUrl(null)
      setIsLoadingLicenseImage(false)

      // Check if we have customer data with a license image
      if (customerData?.isFromDatabase && customerData?.contacts?.[0]) {
        const contact: CustomerContact = customerData.contacts[0]!
        const licenseImageFilename = contact.licenseImage

        if (licenseImageFilename) {
          try {
            setIsLoadingLicenseImage(true)
            const imageUrl = await getImageUrl(licenseImageFilename)
            setLicenseImageUrl(imageUrl)
          } catch (error) {
            console.error('Error loading license image:', error)
            // Don't show error to user since this is supplementary information
          } finally {
            setIsLoadingLicenseImage(false)
          }
        }
      }
    }

    loadLicenseImage()
  }, [customerData?.isFromDatabase, customerData?.contacts])

  const showUseCustomerButton = shouldShowUseCustomerButton()

  // Memoize display fields to prevent rendering issues with intermediate states
  const displayFields = useMemo(() => {
    if (!customerData) return {} as LicenseData

    // Priority 1: If explicitly marked as database customer with contacts, use database data
    if (
      customerData.isFromDatabase &&
      customerData.contacts &&
      Array.isArray(customerData.contacts) &&
      customerData.contacts[0]
    ) {
      const c = customerData.contacts[0]
      return {
        FirstName: c.firstName || '',
        LastName: c.lastName || '',
        LicenseNumber: c.licenseNumber || '',
        ExpirationDate: c.licenseExpiration ? new Date(c.licenseExpiration).toLocaleDateString() : '',
        Address1: c.address1 || '',
        Address2: c.address2 || '',
        City: c.city || '',
        State: c.state || '',
        Zip: c.zip || '',
        BirthDate: c.birthDate ? new Date(c.birthDate).toLocaleDateString() : '',
        Gender: c.gender || '',
      }
    }

    // Priority 2: If has database ID and contacts, use database data
    if (customerData.id && customerData.contacts && Array.isArray(customerData.contacts) && customerData.contacts[0]) {
      const c = customerData.contacts[0]
      return {
        FirstName: c.firstName || '',
        LastName: c.lastName || '',
        LicenseNumber: c.licenseNumber || '',
        ExpirationDate: c.licenseExpiration ? new Date(c.licenseExpiration).toLocaleDateString() : '',
        Address1: c.address1 || '',
        Address2: c.address2 || '',
        City: c.city || '',
        State: c.state || '',
        Zip: c.zip || '',
        BirthDate: c.birthDate ? new Date(c.birthDate).toLocaleDateString() : '',
        Gender: c.gender || '',
      }
    }

    // Priority 3: Fall back to license scan data
    return {
      FirstName: customerData.FirstName || '',
      LastName: customerData.LastName || '',
      LicenseNumber: customerData.LicenseNumber || '',
      ExpirationDate: customerData.ExpirationDate || '',
      Address1: customerData.Address1 || '',
      Address2: customerData.Address2 || '',
      City: customerData.City || '',
      State: customerData.State || '',
      Zip: customerData.Zip || '',
      BirthDate: customerData.BirthDate || '',
      Gender: customerData.Gender || '',
    }
  }, [customerData, customerResultsReturned]) // Re-calculate when customerData or query status changes

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={handleCloseModal}
      PaperProps={{ sx: { maxWidth: 1024, width: '100%' } }}
      SlideProps={{
        direction: 'left',
      }}
    >
      <Box role="presentation">
        {isProcessing && <Loading />}
        <Stack height="100vh">
          <FormModalHeading title="Scan Driver's License" backButtonPosition="right" onBack={handleCloseModal} />
          <Divider />
          <Box px={4} py={2} flexGrow={1}>
            {/* Mode Selection */}
            <Box mb={3}>
              <ToggleButtonGroup
                value={scanMode}
                exclusive
                onChange={handleModeChange}
                aria-label="scan mode"
                size="small"
              >
                <ToggleButton value="photo" aria-label="photo scan">
                  Photo Scan
                </ToggleButton>
                <ToggleButton value="barcode" aria-label="barcode scan">
                  Barcode Scan
                </ToggleButton>
              </ToggleButtonGroup>

              {/* Camera Selection - only show in photo mode */}
              {scanMode === 'photo' && availableCameras.length > 1 && (
                <Box mt={2}>
                  <Typography variant="body2" gutterBottom>
                    Camera:
                  </Typography>
                  <TextField
                    select
                    size="small"
                    value={currentCameraId}
                    onChange={e => switchCamera(e.target.value)}
                    sx={{ minWidth: 200, mr: 2 }}
                  >
                    {availableCameras.map(camera => (
                      <MenuItem key={camera.deviceId} value={camera.deviceId}>
                        {camera.label || `Camera ${availableCameras.indexOf(camera) + 1}`}
                      </MenuItem>
                    ))}
                  </TextField>

                  {/* Quick camera switch buttons */}
                  {isMobileDevice() && (
                    <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={switchToBackCamera}
                        disabled={
                          !availableCameras.some(
                            camera =>
                              camera.label.toLowerCase().includes('back') ||
                              camera.label.toLowerCase().includes('environment'),
                          )
                        }
                      >
                        📷 Back Camera
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={switchToFrontCamera}
                        disabled={
                          !availableCameras.some(
                            camera =>
                              camera.label.toLowerCase().includes('front') ||
                              camera.label.toLowerCase().includes('user'),
                          )
                        }
                      >
                        🤳 Front Camera
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            {!capturedImage && !customerData ? (
              <Grid container>
                {scanMode === 'photo' ? (
                  <>
                    <Grid item xs={7}>
                      <Typography gutterBottom variant="h5">
                        Front of License
                      </Typography>
                      <Typography>
                        Position the front of the driver's license within the frame. This photo will be used to populate
                        customer information.
                      </Typography>
                    </Grid>
                    <Box sx={{ width: '100%', height: 300, position: 'relative' }}>
                      {isCapturing ? (
                        <Webcam
                          ref={webcamRef}
                          screenshotFormat="image/jpeg"
                          screenshotQuality={1}
                          forceScreenshotSourceSize
                          height={300}
                          width={500}
                          videoConstraints={{
                            height: 1100,
                            width: 1710,
                            deviceId: currentCameraId ? { exact: currentCameraId } : undefined,
                            facingMode: !currentCameraId ? 'environment' : undefined,
                          }}
                          onUserMediaError={(error: string | DOMException) => {
                            console.error('Webcam error:', error)
                            setCameraError('Camera access denied or unavailable')
                          }}
                        />
                      ) : null}

                      {/* Camera error display */}
                      {cameraError && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: 'rgba(255,0,0,0.1)',
                            padding: 2,
                            borderRadius: 1,
                            textAlign: 'center',
                          }}
                        >
                          <Typography color="error" variant="body2">
                            {cameraError}
                          </Typography>
                          <Button size="small" onClick={enumerateCameras} sx={{ mt: 1 }}>
                            Try Again
                          </Button>
                        </Box>
                      )}
                    </Box>
                    {isCapturing && (
                      <Button variant="contained" onClick={captureImage} sx={{ mt: 2 }}>
                        Capture
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    <Grid item xs={12}>
                      <Typography gutterBottom variant="h5">
                        Scan License Barcode
                      </Typography>
                      <Typography mb={3}>
                        Use a barcode scanner to scan the barcode on the back of the driver's license.
                      </Typography>

                      {/* Hidden input for barcode scanner */}
                      <TextField
                        ref={barcodeInputRef}
                        value={barcodeData}
                        onChange={handleBarcodeInputChange}
                        onKeyPress={handleBarcodeInputKeyPress}
                        sx={{
                          position: 'absolute',
                          left: '-9999px',
                          opacity: 0,
                          pointerEvents: 'none',
                        }}
                        autoFocus
                      />

                      {/* Visual scanning interface */}
                      <Card
                        sx={{
                          minHeight: 200,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: isScanning ? '2px solid' : '2px dashed',
                          borderColor: isScanning ? 'primary.main' : 'divider',
                          backgroundColor: isScanning ? 'primary.50' : 'background.default',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                        }}
                        onClick={() => barcodeInputRef.current?.focus()}
                      >
                        <CardContent sx={{ textAlign: 'center' }}>
                          {isScanning ? (
                            <>
                              <CircularProgress sx={{ mb: 2 }} />
                              <Typography variant="h6" color="primary">
                                Scanning in progress...
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {barcodeData.length > 0 && `${barcodeData.length} characters captured`}
                              </Typography>
                            </>
                          ) : (
                            <>
                              <Box
                                sx={{
                                  width: 48,
                                  height: 48,
                                  mx: 'auto',
                                  mb: 2,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: '2px dashed',
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                }}
                              >
                                <QrCodeScannerIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                              </Box>
                              <Typography variant="h6" color="text.secondary">
                                Ready to scan
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Point your barcode scanner at the license barcode
                              </Typography>
                            </>
                          )}
                        </CardContent>
                      </Card>

                      {/* Barcode Quality Feedback */}
                      {barcodeQualityIssues && (
                        <Card
                          sx={{ mt: 2, backgroundColor: 'warning.50', border: '1px solid', borderColor: 'warning.200' }}
                        >
                          <CardContent>
                            <Typography variant="h6" color="warning.main" gutterBottom>
                              ⚠️ Scan Quality Issues Detected
                            </Typography>
                            {barcodeQualityIssues.issues.map((issue, index) => (
                              <Typography key={index} variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                • {issue}
                              </Typography>
                            ))}
                            <Typography variant="body2" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                              Suggestions:
                            </Typography>
                            {barcodeQualityIssues.suggestions.map((suggestion, index) => (
                              <Typography key={index} variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                • {suggestion}
                              </Typography>
                            ))}
                          </CardContent>
                        </Card>
                      )}
                    </Grid>
                  </>
                )}
              </Grid>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  {capturedImage && (
                    <img src={capturedImage} alt="Captured license" style={{ maxWidth: '50%', height: 'auto' }} />
                  )}
                  {barcodeData && scanMode === 'barcode' && customerData && (
                    <Card
                      sx={{ mb: 2, backgroundColor: 'success.50', border: '1px solid', borderColor: 'success.200' }}
                    >
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="h6" color="success.main" gutterBottom>
                              ✓ Barcode Successfully Scanned
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {barcodeData.length} characters processed from AAMVA barcode.
                            </Typography>
                          </Box>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              // Clear any pending processing timeout
                              if (processingTimeoutRef.current) {
                                clearTimeout(processingTimeoutRef.current)
                                processingTimeoutRef.current = null
                              }

                              // Reset all barcode-related state
                              setBarcodeData('')
                              setIsScanning(true)
                              setIsProcessing(false)
                              setLastProcessedBarcode('')
                              setCustomerData(null)
                              setCustomerResultsReturned(null)

                              // Reset the lazy query to clear any cached results
                              customerQueryResult.reset?.()

                              // Clear and re-focus the hidden input
                              if (barcodeInputRef.current) {
                                barcodeInputRef.current.value = ''
                                // Clear any existing focus timeout
                                if (focusTimeoutRef.current) {
                                  clearTimeout(focusTimeoutRef.current)
                                  focusTimeoutRef.current = null
                                }
                                focusTimeoutRef.current = setTimeout(() => {
                                  barcodeInputRef.current?.focus()
                                  focusTimeoutRef.current = null
                                }, 100)
                              }
                            }}
                          >
                            Scan Another
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  )}
                </Grid>
                {customerData ? (
                  <>
                    <Stack direction="row" spacing={2} padding={2}>
                      <Grid item xs={isLoadingLicenseImage || licenseImageUrl ? 6 : 12}>
                        <Typography variant="h6">Customer Information</Typography>
                        <>
                          <Typography>
                            Name: {displayFields.FirstName} {displayFields.LastName}
                          </Typography>
                          <Typography>License: {displayFields.LicenseNumber}</Typography>
                          <Typography>Address: {displayFields.Address1}</Typography>
                          {displayFields.Address2 && <Typography>{displayFields.Address2}</Typography>}
                          <Typography>
                            {displayFields.City}, {displayFields.State} {displayFields.Zip}
                          </Typography>
                          {displayFields.BirthDate && <Typography>Birth Date: {displayFields.BirthDate}</Typography>}
                          {displayFields.Gender && <Typography>Gender: {displayFields.Gender}</Typography>}
                        </>
                      </Grid>

                      {/* License Image Display */}
                      {customerData?.isFromDatabase && (isLoadingLicenseImage || licenseImageUrl) && (
                        <Grid item xs={6}>
                          <Typography variant="h6" sx={{ mb: 2 }}>
                            License Image
                          </Typography>
                          <Box
                            sx={{
                              maxWidth: 400,
                              borderRadius: 1,
                              overflow: 'hidden',
                            }}
                          >
                            {isLoadingLicenseImage ? (
                              <Skeleton variant="rectangular" width={400} height={250} animation="wave" />
                            ) : licenseImageUrl ? (
                              <img
                                src={licenseImageUrl}
                                alt="Driver's License"
                                style={{
                                  width: '100%',
                                  height: 'auto',
                                  maxHeight: 300,
                                  objectFit: 'contain',
                                }}
                              />
                            ) : null}
                          </Box>
                        </Grid>
                      )}
                    </Stack>
                    <Grid item xs={12}>
                      <Stack direction="row" spacing={2}>
                        {customerResultsReturned && customerResultsReturned > 0 ? (
                          <>
                            {showUseCustomerButton && (
                              <Button variant="contained" onClick={handleSelectCustomer}>
                                Use This Customer
                              </Button>
                            )}
                            <Button
                              variant={showUseCustomerButton ? 'outlined' : 'contained'}
                              color="primary"
                              onClick={handleCreateNewTicket}
                            >
                              Create New Ticket
                            </Button>
                          </>
                        ) : (
                          <Stack direction="column" spacing={2}>
                            <Typography variant="h6">No customers found</Typography>
                            <Button variant="contained" onClick={handleSelectCustomer}>
                              Create New Customer
                            </Button>
                          </Stack>
                        )}
                      </Stack>
                    </Grid>
                    {recentTickets.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                          Recent Tickets
                        </Typography>
                        <TableContainer component={Paper} sx={{ mb: 2 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Ticket ID</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Materials</TableCell>
                                <TableCell align="right">Net Weight</TableCell>
                                <TableCell align="right">Net Value</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {recentTickets.map(ticket => (
                                <TableRow key={ticket.id}>
                                  <TableCell>
                                    <Typography
                                      sx={{ cursor: 'pointer', color: 'primary.main' }}
                                      onClick={() => navigate(`/app/purchases/ticket-library/${ticket.id}`)}
                                    >
                                      {ticket.ticketId}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>{new Date(ticket.effectiveDate).toLocaleDateString()}</TableCell>
                                  <TableCell>{ticket.status.value}</TableCell>
                                  <TableCell>{ticket.materialTypeCommonNames?.join(', ')}</TableCell>
                                  <TableCell align="right">{ticket.netWeight?.toLocaleString()} lbs</TableCell>
                                  <TableCell align="right">{ticket.netValue?.commonString || '$0.00'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>
                    )}
                  </>
                ) : (
                  <Grid item xs={12}>
                    <Stack direction="row" spacing={2}>
                      {scanMode === 'photo' && (
                        <>
                          <Button variant="contained" onClick={handleTakeAnotherPhoto}>
                            Take Another Photo
                          </Button>
                          <Button variant="outlined" onClick={handleTryAgain}>
                            Try Again
                          </Button>
                        </>
                      )}
                    </Stack>
                  </Grid>
                )}
              </Grid>
            )}
          </Box>
          <Divider />
          <Stack direction="row" p={2} spacing={2}>
            <Button variant="outlined" onClick={handleCloseModal}>
              Cancel
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Drawer>
  )
}

export default LicenseScanModal
